import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytes
} from "firebase/storage";
import { ChevronDown, ChevronRight, ChevronUp, FileAudio, Pencil, Play, Trash2, Upload, X } from "lucide-react";
import { triggerTtsEvalRun } from "../../../helpers/AI_functions";
import GeneratedAudioSection from "./GeneratedAudioSection";

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  padding: 20
};

const primaryText = "#020617";
const secondaryText = "#64748b";
const mutedBg = "#f1f5f9";

const sanitizeFileName = (name) => String(name || "audio.mp3").replace(/[^a-zA-Z0-9._-]/g, "_");

const getAudioExtension = (name) => {
  const match = String(name || "").match(/\.(mp3|m4a)$/i);
  return match ? match[1].toLowerCase() : "mp3";
};

const getExtensionFromUpload = (upload) => {
  const match = String(upload?.objectPath || upload?.fileName || "").match(/\.(mp3|m4a)$/i);
  return match ? match[1].toLowerCase() : "mp3";
};

const formatTimestamp = (seconds) => {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
};

const parseTimestamp = (timestamp) => {
  const parts = String(timestamp || "").trim().split(":").map(Number);
  if (parts.length < 1 || parts.length > 3 || parts.some((part) => !Number.isFinite(part) || part < 0)) return null;

  return parts.reduce((total, part) => total * 60 + part, 0);
};

const getIssueStartSeconds = (issue) => {
  const errorWindowStart = String(issue?.error_window || "").split(/[-–—]/, 1)[0];
  const parsedStart = parseTimestamp(errorWindowStart);
  if (parsedStart !== null) return parsedStart;

  const legacyTimestamp = Number(issue?.timestampSec);
  return Number.isFinite(legacyTimestamp) && legacyTimestamp >= 0 ? legacyTimestamp : 0;
};

const getIssueTimeLabel = (issue) => {
  const errorWindow = String(issue?.error_window || "").trim();
  return errorWindow || formatTimestamp(getIssueStartSeconds(issue));
};

const flattenIssues = (report) => {
  const groups = report?.error_types || [];
  return groups.flatMap((group, groupIndex) =>
    (group.details || []).map((detail, detailIndex) => ({
      ...detail,
      groupIndex,
      detailIndex,
      mistake: group.mistake,
      priority: Number(group.priority) || 3,
      key: `${groupIndex}-${detailIndex}`
    }))
  );
};

const countIssuesByPriority = (report, priority) =>
  flattenIssues(report).filter((issue) => issue.priority === priority).length;

const formatFileSize = (bytes) => {
  const size = Number(bytes) || 0;
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  return `${Math.max(1, Math.round(size / 1024))} KB`;
};

const formatCreatedAt = (createdAt) => {
  const date = createdAt?.toDate ? createdAt.toDate() : null;
  if (!date) return "";
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const PriorityBadge = ({ priority }) => {
  const label = priority === 1 ? "Critical" : priority === 2 ? "Major" : "Minor";
  const styles = {
    1: { background: "#fee2e2", color: "#dc2626", border: "#fca5a5" },
    2: { background: "#fef3c7", color: "#b45309", border: "#fcd34d" },
    3: { background: "#f1f5f9", color: "#64748b", border: "#e5e7eb" }
  };
  const s = styles[priority] || styles[3];
  return (
    <span style={{ border: `1px solid ${s.border}`, background: s.background, color: s.color, borderRadius: 999, padding: "3px 8px", fontSize: 12, fontWeight: 700, lineHeight: 1.2, whiteSpace: "nowrap" }}>
      P{priority} · {label}
    </span>
  );
};

const IssueCard = ({ issue, effectivePriority, expanded, onToggle, onPlay, onPriorityChange }) => (
  <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
    <div style={{ display: "grid", gap: 8, padding: 12 }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          width: "100%",
          padding: 0,
          border: "none",
          background: "transparent",
          color: primaryText,
          cursor: "pointer",
          textAlign: "left"
        }}
      >
        <span style={{ marginTop: 1, flex: "0 0 auto" }}>
          {expanded ? <ChevronDown size={24} /> : <ChevronRight size={24} />}
        </span>
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: "block", fontWeight: 700, fontSize: 14, color: primaryText, lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{issue.error_window || issue.where || "Audio issue"}</span>
          <span style={{ display: "block", color: secondaryText, fontSize: 12, lineHeight: 1.35, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {issue.mistake}
          </span>
        </span>
      </button>
      <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 24, flexWrap: "wrap" }}>
        <button
          type="button"
          title={`Play from ${formatTimestamp(getIssueStartSeconds(issue))}`}
          onClick={(e) => {
            e.stopPropagation();
            onPlay();
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            height: 28,
            padding: "0 8px",
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            background: "#fff",
            color: primaryText,
            cursor: "pointer",
            fontWeight: 600,
            fontSize: 12
          }}
        >
          <Play size={14} />
          {getIssueTimeLabel(issue)}
        </button>
        <PriorityBadge priority={effectivePriority} />
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4 }}>
        <button
          type="button"
          title="Promote priority"
          disabled={effectivePriority <= 1}
          onClick={(e) => {
            e.stopPropagation();
            onPriorityChange(-1);
          }}
          style={{ width: 28, height: 28, borderRadius: 7, border: "none", background: "#fff", color: effectivePriority <= 1 ? "#cbd5e1" : primaryText, cursor: effectivePriority <= 1 ? "default" : "pointer", display: "grid", placeItems: "center" }}
        >
          <ChevronUp size={16} />
        </button>
        <button
          type="button"
          title="Demote priority"
          disabled={effectivePriority >= 3}
          onClick={(e) => {
            e.stopPropagation();
            onPriorityChange(1);
          }}
          style={{ width: 28, height: 28, borderRadius: 7, border: "none", background: "#fff", color: effectivePriority >= 3 ? "#cbd5e1" : primaryText, cursor: effectivePriority >= 3 ? "default" : "pointer", display: "grid", placeItems: "center" }}
        >
          <ChevronDown size={16} />
        </button>
        </div>
      </div>
    </div>
    {expanded && (
      <div style={{ borderTop: "1px solid #e5e7eb", padding: 12, display: "grid", gap: 8, background: "#fff", color: secondaryText, fontSize: 14, lineHeight: 1.45 }}>
        <div><strong style={{ color: primaryText }}>Expected:</strong> {issue.expected}</div>
        <div><strong style={{ color: primaryText }}>Actual:</strong> {issue.actual}</div>
        <div><strong style={{ color: primaryText }}>Why:</strong> {issue.explanation}</div>
      </div>
    )}
  </div>
);

const ScoreReport = ({ upload, audioRefs, priorityOverrides, setPriorityOverrides }) => {
  const [showMore, setShowMore] = useState(false);
  const [expanded, setExpanded] = useState({});
  const issues = useMemo(() => flattenIssues(upload.report), [upload.report]);

  const getPriority = (issue) => {
    const key = `${upload.id}:${issue.key}`;
    return priorityOverrides[key] || issue.priority;
  };

  const setPriority = (issue, delta) => {
    const key = `${upload.id}:${issue.key}`;
    setPriorityOverrides((prev) => {
      const current = prev[key] || issue.priority;
      const next = Math.min(3, Math.max(1, current + delta));
      return { ...prev, [key]: next };
    });
  };

  const priorityGroups = [1, 2, 3]
    .map((priority) => ({
      priority,
      issues: issues.filter((issue) => getPriority(issue) === priority)
    }))
    .filter((group) => group.issues.length > 0);
  const priorityOneGroup = priorityGroups.find((group) => group.priority === 1);
  const lowerPriorityGroups = priorityGroups.filter((group) => group.priority > 1);
  const hasLowerPriorityIssues = issues.some((issue) => getPriority(issue) > 1);
  const lowerPriorityCount = issues.filter((issue) => getPriority(issue) > 1).length;

  const renderGroup = (group) => (
    <div key={group.priority} style={{ display: "grid", gap: 8 }}>
      <div style={{ fontWeight: 700, fontSize: 12, color: secondaryText, textTransform: "uppercase", letterSpacing: "0.06em" }}>
        Priority {group.priority} issues
      </div>
      {group.issues.map((issue) => (
        <IssueCard
          key={issue.key}
          issue={issue}
          effectivePriority={getPriority(issue)}
          expanded={!!expanded[issue.key]}
          onToggle={() => setExpanded((prev) => ({ ...prev, [issue.key]: !prev[issue.key] }))}
          onPriorityChange={(delta) => setPriority(issue, delta)}
          onPlay={() => {
            const player = audioRefs.current[upload.id];
            if (!player) return;
            player.currentTime = getIssueStartSeconds(issue);
            player.play().catch(() => {});
          }}
        />
      ))}
    </div>
  );

  return (
    <div style={{ display: "grid", gap: 12 }}>
      {priorityOneGroup ? (
        renderGroup(priorityOneGroup)
      ) : (
        <div style={{ color: "#065f46", fontWeight: 800 }}>No priority 1 issues found.</div>
      )}
      {hasLowerPriorityIssues && (
        <button
          type="button"
          onClick={() => setShowMore((prev) => !prev)}
          style={{ justifySelf: "start", display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 8px", border: "none", background: "#fff", color: primaryText, fontWeight: 600, cursor: "pointer", fontSize: 14 }}
        >
          {showMore ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          {showMore ? "Hide lower-priority issues" : `Show ${lowerPriorityCount} more lower-priority issues`}
        </button>
      )}
      {showMore && lowerPriorityGroups.map(renderGroup)}
    </div>
  );
};

const AudioReviewTab = ({ isActive, podcastId, nodeId, selectedLanguage }) => {
  const db = useMemo(() => getFirestore(), []);
  const storage = useMemo(() => getStorage(), []);
  const langCode = String(selectedLanguage || "EN").toUpperCase();
  const nodeIdStr = nodeId != null ? String(nodeId) : "";
  const audioRefs = useRef({});

  const [script, setScript] = useState("");
  const [scriptDraft, setScriptDraft] = useState("");
  const [scriptDialogOpen, setScriptDialogOpen] = useState(false);
  const [scriptLoading, setScriptLoading] = useState(false);
  const [scriptSaving, setScriptSaving] = useState(false);
  const [scriptStatus, setScriptStatus] = useState("");
  const [scriptError, setScriptError] = useState("");
  const [uploads, setUploads] = useState([]);
  const [uploadsError, setUploadsError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [removingId, setRemovingId] = useState("");
  const [evaluatingId, setEvaluatingId] = useState("");
  const [evalStatusById, setEvalStatusById] = useState({});
  const [priorityOverrides, setPriorityOverrides] = useState({});

  const narrativeRef = useMemo(() => {
    if (!podcastId || !nodeIdStr) return null;
    return doc(db, "podcasts", String(podcastId), "travel_narrative", nodeIdStr);
  }, [db, podcastId, nodeIdStr]);

  const uploadsCollectionRef = useMemo(() => {
    if (!podcastId || !nodeIdStr || !langCode) return null;
    return collection(db, "podcasts", String(podcastId), "audio_review", nodeIdStr, langCode);
  }, [db, podcastId, nodeIdStr, langCode]);

  const loadScript = useCallback(async () => {
    if (!narrativeRef) return;
    try {
      setScriptLoading(true);
      setScriptError("");
      setScriptStatus("");
      const snap = await getDoc(narrativeRef);
      const narrative = snap.exists() ? String(snap.data()?.narrative || "") : "";
      setScript(narrative);
      setScriptDraft(narrative);
      setScriptStatus(snap.exists() ? "Loaded." : "No travel narrative found yet.");
    } catch (err) {
      console.error("Failed to load travel narrative:", err);
      setScriptError("Failed to load script.");
    } finally {
      setScriptLoading(false);
    }
  }, [narrativeRef]);

  useEffect(() => {
    if (!isActive) return;
    loadScript();
  }, [isActive, loadScript]);

  useEffect(() => {
    if (!isActive || !uploadsCollectionRef) {
      setUploads([]);
      return undefined;
    }

    setUploadsError("");
    const q = query(uploadsCollectionRef, orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      async (snap) => {
        const rows = await Promise.all(
          snap.docs.map(async (uploadDoc) => {
            const data = uploadDoc.data() || {};
            let url = "";
            if (data.objectPath) {
              url = await getDownloadURL(storageRef(storage, data.objectPath)).catch(() => "");
            }
            return { id: uploadDoc.id, ...data, url };
          })
        );
        setUploads(rows);
      },
      (err) => {
        console.error("Failed to listen for audio review uploads:", err);
        setUploadsError("Failed to load audio uploads.");
      }
    );

    return unsub;
  }, [isActive, uploadsCollectionRef, storage]);

  if (!isActive) return null;

  const saveScript = async () => {
    if (!narrativeRef) return;
    try {
      setScriptSaving(true);
      setScriptError("");
      setScriptStatus("");
      await setDoc(
        narrativeRef,
        {
          narrative: String(scriptDraft || ""),
          updatedAt: serverTimestamp(),
          nodeId: nodeIdStr
        },
        { merge: true }
      );
      setScript(String(scriptDraft || ""));
      setScriptDialogOpen(false);
      setScriptStatus("Saved.");
    } catch (err) {
      console.error("Failed to save travel narrative:", err);
      setScriptError("Failed to save script.");
    } finally {
      setScriptSaving(false);
    }
  };

  const uploadFiles = async (files) => {
    const fileList = Array.from(files || []);
    if (!uploadsCollectionRef || !podcastId || !nodeIdStr || fileList.length === 0) return;

    try {
      setUploading(true);
      setUploadStatus("");
      setUploadsError("");

      for (const file of fileList) {
        const extOk = /\.(mp3|m4a)$/i.test(file.name || "");
        if (!extOk) continue;

        const uploadDocRef = doc(uploadsCollectionRef);
        const fileName = sanitizeFileName(file.name);
        const extension = getAudioExtension(file.name);
        const objectPath = `Podcasts/${String(podcastId)}/${langCode}/${nodeIdStr}/audio-review/${uploadDocRef.id}.${extension}`;
        const contentType = file.type || (extension === "m4a" ? "audio/mp4" : "audio/mpeg");

        await uploadBytes(storageRef(storage, objectPath), file, { contentType });
        await setDoc(uploadDocRef, {
          fileName,
          objectPath,
          contentType,
          size: file.size || 0,
          language: langCode,
          nodeId: nodeIdStr,
          report: {},
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }

      setUploadStatus("Uploaded.");
    } catch (err) {
      console.error("Failed to upload audio review file:", err);
      setUploadsError("Failed to upload audio.");
    } finally {
      setUploading(false);
    }
  };

  const removeUpload = async (upload) => {
    if (!uploadsCollectionRef || !upload?.id) return;
    try {
      setRemovingId(upload.id);
      setUploadsError("");
      if (upload.objectPath) {
        await deleteObject(storageRef(storage, upload.objectPath)).catch((err) => {
          if (err?.code !== "storage/object-not-found") throw err;
        });
      }
      await deleteDoc(doc(uploadsCollectionRef, upload.id));
    } catch (err) {
      console.error("Failed to remove audio upload:", err);
      setUploadsError("Failed to remove upload.");
    } finally {
      setRemovingId("");
    }
  };

  const runEval = async (upload) => {
    if (!uploadsCollectionRef || !upload?.id || !podcastId || !nodeIdStr) return;

    try {
      setEvaluatingId(upload.id);
      setEvalStatusById((prev) => ({ ...prev, [upload.id]: { type: "info", text: "Submitting eval..." } }));

      const audioId = String(upload.id);
      const extension = getExtensionFromUpload(upload);
      const audioPath = upload.objectPath || `Podcasts/${String(podcastId)}/${langCode}/${nodeIdStr}/audio-review/${audioId}.${extension}`;

      const result = await triggerTtsEvalRun({
        podcast_id: String(podcastId),
        node_id: nodeIdStr,
        language: langCode,
        audioId,
        audioPath
      });

      await setDoc(
        doc(uploadsCollectionRef, upload.id),
        {
          evalAudioId: audioId,
          evalAudioPath: audioPath,
          evalRun: result,
          evalSubmittedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );

      setEvalStatusById((prev) => ({ ...prev, [upload.id]: { type: "success", text: "Eval submitted." } }));
    } catch (err) {
      console.error("Failed to submit TTS eval:", err);
      setEvalStatusById((prev) => ({
        ...prev,
        [upload.id]: { type: "error", text: err?.message || "Failed to submit eval." }
      }));
    } finally {
      setEvaluatingId("");
    }
  };

  return (
    <div style={{ display: "grid", gap: 24 }}>
      <div style={cardStyle}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: primaryText }}>Script</div>
            <div style={{ color: secondaryText, fontSize: 14, lineHeight: 1.45, marginTop: 4, maxWidth: 520 }}>
              The reference script used to score uploaded audio. Open it to view or edit.
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              setScriptDraft(script);
              setScriptDialogOpen(true);
            }}
            disabled={scriptLoading || !narrativeRef}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              background: "#fff",
              color: primaryText,
              fontWeight: 600,
              fontSize: 14,
              cursor: scriptLoading || !narrativeRef ? "default" : "pointer",
              whiteSpace: "nowrap"
            }}
          >
            <Pencil size={16} />
            {scriptLoading ? "Loading..." : "Edit script"}
          </button>
        </div>
        {(scriptStatus || scriptError) && (
          <div style={{ marginTop: 12, color: scriptError ? "crimson" : "#065f46", fontWeight: 700 }}>
            {scriptError || scriptStatus}
          </div>
        )}
      </div>

      {langCode === "EN" ? (
        <GeneratedAudioSection
          podcastId={podcastId}
          nodeId={nodeIdStr}
          language={langCode}
          script={script}
        />
      ) : null}

      <div style={cardStyle}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: primaryText }}>Audio uploads</div>
            <div style={{ color: secondaryText, fontSize: 14, lineHeight: 1.45, marginTop: 4, maxWidth: 560 }}>
              Upload one or more .m4a or .mp3 files. Scores are generated per file.
            </div>
          </div>
          <label
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid #111827",
              background: uploading ? "#475569" : "#020617",
              color: "#fff",
              fontWeight: 600,
              fontSize: 14,
              cursor: uploading || !uploadsCollectionRef ? "default" : "pointer",
              whiteSpace: "nowrap"
            }}
          >
            <Upload size={16} />
            {uploading ? "Uploading..." : "Upload audio"}
            <input
              type="file"
              accept=".mp3,.m4a,audio/mpeg,audio/mp4,audio/x-m4a"
              multiple
              disabled={uploading || !uploadsCollectionRef}
              onChange={(e) => {
                uploadFiles(e.target.files);
                e.target.value = "";
              }}
              style={{ display: "none" }}
            />
          </label>
        </div>

        {(uploadStatus || uploadsError) && (
          <div style={{ marginTop: 10, color: uploadsError ? "crimson" : "#065f46", fontWeight: 700 }}>
            {uploadsError || uploadStatus}
          </div>
        )}

        <div style={{ display: "grid", gap: 16, marginTop: 16 }}>
          {uploads.length === 0 && (
            <div style={{ color: secondaryText, border: "1px dashed #e5e7eb", borderRadius: 8, padding: 32, textAlign: "center", fontSize: 14 }}>
              No audio uploads for this node yet.
            </div>
          )}
          {uploads.map((upload) => (
            <div key={upload.id} style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden", display: "grid", background: "#fff" }}>
              <div style={{ padding: 16, display: "grid", gap: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                  <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 8, background: mutedBg, display: "grid", placeItems: "center", color: secondaryText, flex: "0 0 auto" }}>
                      <FileAudio size={20} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, color: primaryText, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{upload.fileName}</div>
                      <div style={{ color: secondaryText, fontSize: 12, marginTop: 2 }}>
                        {[formatFileSize(upload.size), formatCreatedAt(upload.createdAt)].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ border: "1px solid #fca5a5", background: "#fee2e2", color: "#dc2626", borderRadius: 999, padding: "3px 8px", fontSize: 12, fontWeight: 700, lineHeight: 1.2 }}>
                      {countIssuesByPriority(upload.report, 1)} P1
                    </span>
                    <button
                      type="button"
                      onClick={() => runEval(upload)}
                      disabled={evaluatingId === upload.id}
                      style={{
                        padding: "7px 10px",
                        borderRadius: 8,
                        border: "1px solid #111827",
                        background: evaluatingId === upload.id ? "#475569" : "#020617",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 13,
                        cursor: evaluatingId === upload.id ? "default" : "pointer",
                        lineHeight: 1
                      }}
                    >
                      {evaluatingId === upload.id ? "Eval..." : "Eval"}
                    </button>
                    <button
                      type="button"
                      title="Remove upload"
                      onClick={() => removeUpload(upload)}
                      disabled={removingId === upload.id}
                      style={{ display: "grid", placeItems: "center", width: 32, height: 32, borderRadius: 8, border: "none", background: "#fff", color: primaryText, cursor: removingId === upload.id ? "default" : "pointer" }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                {removingId === upload.id && <div style={{ color: "#991b1b", fontWeight: 800 }}>Removing...</div>}
                {evalStatusById[upload.id] && (
                  <div style={{ color: evalStatusById[upload.id].type === "error" ? "crimson" : "#065f46", fontWeight: 700 }}>
                    {evalStatusById[upload.id].text}
                  </div>
                )}
                {upload.url ? (
                  <audio
                    ref={(el) => {
                      if (el) audioRefs.current[upload.id] = el;
                    }}
                    controls
                    src={upload.url}
                    style={{ width: "100%", height: 36 }}
                  />
                ) : (
                  <div style={{ color: "crimson", fontWeight: 700 }}>Audio file could not be loaded.</div>
                )}
              </div>
              <div style={{ borderTop: "1px solid #e5e7eb", padding: 16 }}>
                <ScoreReport
                  upload={upload}
                  audioRefs={audioRefs}
                  priorityOverrides={priorityOverrides}
                  setPriorityOverrides={setPriorityOverrides}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {scriptDialogOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.72)", zIndex: 10000, display: "grid", placeItems: "center", padding: 24 }}>
          <div style={{ width: "95vw", maxWidth: 1400, height: "90vh", background: "#fff", borderRadius: 12, display: "grid", gridTemplateRows: "auto 1fr auto", overflow: "hidden" }}>
            <div style={{ padding: "20px 24px 12px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 18, color: primaryText }}>Edit script</div>
              <button
                type="button"
                aria-label="Close script editor"
                onClick={() => {
                  setScriptDraft(script);
                  setScriptDialogOpen(false);
                }}
                disabled={scriptSaving}
                style={{ display: "grid", placeItems: "center", width: 32, height: 32, border: "none", background: "#fff", color: "#475569", cursor: scriptSaving ? "default" : "pointer" }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: "0 24px", minHeight: 0 }}>
              <textarea
                value={scriptDraft}
                onChange={(e) => setScriptDraft(e.target.value)}
                spellCheck={false}
                style={{
                  width: "100%",
                  height: "100%",
                  minHeight: 0,
                  border: "1px solid #9db2cf",
                  borderRadius: 10,
                  resize: "none",
                  padding: 14,
                  fontSize: 14,
                  lineHeight: 1.5,
                  color: primaryText,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  outline: "none",
                  boxSizing: "border-box"
                }}
              />
            </div>
            <div style={{ padding: "16px 24px 20px", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                type="button"
                onClick={() => {
                  setScriptDraft(script);
                  setScriptDialogOpen(false);
                }}
                disabled={scriptSaving}
                style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff", color: primaryText, fontWeight: 600, fontSize: 14, cursor: scriptSaving ? "default" : "pointer" }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveScript}
                disabled={scriptSaving || !narrativeRef}
                style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #020617", background: scriptSaving ? "#64748b" : "#020617", color: "#fff", fontWeight: 600, fontSize: 14, cursor: scriptSaving ? "default" : "pointer" }}
              >
                {scriptSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioReviewTab;
